'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'whoplays-theme';

type ThemeContextValue = {
  theme: ThemePreference;
  actualTheme: ResolvedTheme;
  toggleTheme: () => void;
  setTheme: (theme: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  actualTheme: 'light',
  toggleTheme: () => undefined,
  setTheme: () => undefined,
});

function getStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') {
    return 'light';
  }
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    return stored;
  }
  return 'system';
}

function resolveTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'system') {
    if (typeof window === 'undefined') return 'light';
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return preference;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themePreference, setThemePreference] = useState<ThemePreference>('light');
  const [actualTheme, setActualTheme] = useState<ResolvedTheme>('light');

  useEffect(() => {
    const initialPreference = getStoredPreference();
    setThemePreference(initialPreference);
    setActualTheme(resolveTheme(initialPreference));
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (themePreference === 'system') {
        setActualTheme(event.matches ? 'dark' : 'light');
      }
    };
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [themePreference]);

  useEffect(() => {
    const resolved = resolveTheme(themePreference);
    setActualTheme(resolved);
    const root = document.documentElement;
    root.dataset.theme = themePreference === 'system' ? resolved : themePreference;
    root.classList.toggle('dark', resolved === 'dark');
    window.localStorage.setItem(THEME_STORAGE_KEY, themePreference);
  }, [themePreference]);

  const setTheme = useCallback((value: ThemePreference) => {
    setThemePreference(value);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemePreference((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'light';
      return actualTheme === 'dark' ? 'light' : 'dark';
    });
  }, [actualTheme]);

  const value = useMemo(
    () => ({
      theme: themePreference,
      actualTheme,
      toggleTheme,
      setTheme,
    }),
    [themePreference, actualTheme, toggleTheme, setTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
